import { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, Navigate, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ScanLine,
  UserPlus,
  Users,
  LogOut,
  ChevronLeft,
  Loader2,
  Camera,
  Sparkles,
  Trash2,
  Pencil,
  Phone,
  Mail,
  Building2,
  Search,
  QrCode,
  MessageCircle,
  Fingerprint,
  ImageUp,
} from "lucide-react";
import { vapi, vAuth, fileToCompressedBase64 } from "./visitorApi";
import { waLink, mailtoLink, shareBoothPhoto, BOOTH_PHOTO_URL, followupMessage } from "./followup";
import { setPwaIdentity } from "../pwaIdentity";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from "@simplewebauthn/browser";
import Scanner from "../mobile/Scanner";
import "./visitor.css";

const DEFAULT_EXHIBITION = "Bharat Buildcon";
const SOURCE_LABEL = { badge_scan: "Badge", card_scan: "Card", manual: "Manual" };

const EMPTY = {
  name: "",
  company: "",
  designation: "",
  phone: "",
  email: "",
  website: "",
  city: "",
  country: "",
  address: "",
  products_interest: "",
  rating: "",
  notes: "",
  exhibition_name: "",
  card_image_id: null,
  source: "manual",
  pass_url: "",
};

export default function VisitorApp() {
  const [user, setUser] = useState(vAuth.getUser());
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    // Install this route's own PWA identity (manifest + iOS apple-touch-icon/title).
    setPwaIdentity({
      manifest: "/visitor.webmanifest",
      title: "Manik Leads",
      themeColor: "#0d9488",
      appleIcon: "/leads-icon-180.png",
    });
  }, []);

  useEffect(() => {
    (async () => {
      const tok = vAuth.getToken();
      if (!tok) {
        setBooted(true);
        return;
      }
      try {
        const me = await vapi.me();
        const p = await vapi.permissions();
        vAuth.setAuth(tok, me, p?.effective_permissions || []);
        setUser(me);
      } catch {
        vAuth.clear();
        setUser(null);
      } finally {
        setBooted(true);
      }
    })();
  }, []);

  if (!booted) {
    return (
      <div className="v-app">
        <div className="v-screen">
          <Loader2 className="v-spin" style={{ margin: "48px auto", display: "block" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="v-app" data-testid="visitor-app">
      <Toaster richColors position="top-center" />
      <Routes>
        <Route path="/" element={user ? <Home user={user} setUser={setUser} /> : <Navigate to="login" />} />
        <Route path="login" element={<Login onLoggedIn={setUser} />} />
        <Route path="pin-setup" element={user ? <PinSetup /> : <Navigate to="login" />} />
        <Route path="scan" element={user ? <ScanCard /> : <Navigate to="login" />} />
        <Route path="badge" element={user ? <BadgeScan /> : <Navigate to="login" />} />
        <Route path="new" element={user ? <CaptureManual /> : <Navigate to="login" />} />
        <Route path="list" element={user ? <VisitorList /> : <Navigate to="login" />} />
        <Route path="v/:id" element={user ? <VisitorDetail /> : <Navigate to="login" />} />
        <Route path="*" element={<Navigate to="/visitors" />} />
      </Routes>
    </div>
  );
}

function Chrome({ title, sub, onBack }) {
  return (
    <div className="v-chrome">
      {onBack && (
        <button className="v-back-btn" onClick={onBack} data-testid="v-back-btn">
          <ChevronLeft size={20} />
        </button>
      )}
      <div style={{ flex: 1 }}>
        <div className="v-chrome-title">{title}</div>
        {sub && <div className="v-chrome-sub">{sub}</div>}
      </div>
    </div>
  );
}

function Login({ onLoggedIn }) {
  const navigate = useNavigate();
  const remembered = vAuth.getRememberedEmail() || "";
  const [mode, setMode] = useState(remembered ? "pin" : "password");
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [passkeyReady, setPasskeyReady] = useState(false);

  const supportsWebAuthn = browserSupportsWebAuthn();

  // Check if the remembered email has a passkey registered (drives the button).
  useEffect(() => {
    let alive = true;
    const e = (remembered || "").trim();
    if (!supportsWebAuthn || !e) {
      setPasskeyReady(false);
      return;
    }
    vapi
      .webauthnAvailable(e)
      .then((r) => alive && setPasskeyReady(!!r.available))
      .catch(() => alive && setPasskeyReady(false));
    return () => {
      alive = false;
    };
  }, [remembered, supportsWebAuthn]);

  const finishLogin = (data) => {
    if (!(data.permissions || []).includes("visitor.capture")) {
      setErr("Your account doesn't have visitor capture access. Ask an admin to grant 'visitor.capture'.");
      setBusy(false);
      return false;
    }
    vAuth.setAuth(data.access_token, data.user, data.permissions || []);
    onLoggedIn(data.user);
    return true;
  };

  const passkeyLogin = async () => {
    const e = email.trim();
    if (!e) {
      setErr("Enter your email first.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const optionsJSON = await vapi.webauthnLoginBegin(e);
      const asseResp = await startAuthentication({ optionsJSON });
      const data = await vapi.webauthnLoginComplete(e, asseResp);
      if (finishLogin(data)) navigate("/visitors");
    } catch (ex) {
      if (ex?.response?.status === 404) {
        setErr("No Face ID / fingerprint set up on this account yet. Sign in with your password, then enable it.");
        setPasskeyReady(false);
      } else if (ex?.name === "NotAllowedError" || ex?.name === "AbortError") {
        setErr("Face ID / fingerprint was cancelled.");
      } else {
        setErr(ex?.response?.data?.detail || "Biometric sign-in failed. Try your password.");
      }
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "pin") {
        const data = await vapi.loginPin(email.trim(), pin);
        if (finishLogin(data)) navigate("/visitors");
      } else {
        const data = await vapi.login(email.trim(), password);
        if (finishLogin(data)) {
          try {
            const st = await vapi.pinStatus();
            if (!st.pin_set) {
              navigate("/visitors/pin-setup");
              return;
            }
          } catch { /* ignore */ }
          navigate("/visitors");
        }
      }
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          (mode === "pin" ? "Invalid PIN or email." : "Login failed. Check your email and password.")
      );
      setBusy(false);
    }
  };

  return (
    <div className="v-screen" style={{ paddingTop: 40 }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div
          style={{
            width: 64, height: 64, borderRadius: 18, margin: "0 auto 14px",
            background: "linear-gradient(145deg,#0d9488,#0f766e)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
        >
          <Users size={32} color="#fff" />
        </div>
        <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 800, fontSize: 24 }}>Manik Leads</div>
        <div className="v-muted">{mode === "pin" ? "Quick login with your PIN" : "Exhibition visitor capture"}</div>
      </div>

      <form onSubmit={submit}>
        {err && <div className="v-error" data-testid="v-login-error">{err}</div>}
        <label className="v-label">Email</label>
        <input
          className="v-input" type="email" value={email} autoComplete="username"
          onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
          data-testid="v-login-email" required
        />

        {mode === "pin" ? (
          <>
            <label className="v-label">PIN</label>
            <input
              className="v-input" type="password" inputMode="numeric" autoComplete="off"
              value={pin} maxLength={8}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="••••" data-testid="v-login-pin" required
              style={{ letterSpacing: "0.4em", textAlign: "center", fontSize: 22 }}
            />
          </>
        ) : (
          <>
            <label className="v-label">Password</label>
            <input
              className="v-input" type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              data-testid="v-login-password" required
            />
          </>
        )}

        <div style={{ height: 18 }} />
        <button className="v-btn" disabled={busy} data-testid="v-login-submit">
          {busy ? <Loader2 className="v-spin" size={18} /> : mode === "pin" ? "Unlock" : "Sign in"}
        </button>
      </form>

      {passkeyReady && (
        <button
          type="button"
          className="v-btn v-btn--ghost"
          disabled={busy}
          onClick={passkeyLogin}
          data-testid="v-login-passkey"
          style={{
            marginTop: 12, display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8,
          }}
        >
          <Fingerprint size={18} /> Sign in with Face ID / Fingerprint
        </button>
      )}

      <div style={{ textAlign: "center", marginTop: 14 }}>
        {mode === "pin" ? (
          <button className="v-link-btn" onClick={() => { setMode("password"); setErr(""); }} data-testid="v-switch-password">
            Use email & password instead
          </button>
        ) : (
          <button className="v-link-btn" onClick={() => { setMode("pin"); setErr(""); }} data-testid="v-switch-pin">
            Use PIN instead
          </button>
        )}
      </div>
    </div>
  );
}

function PinSetup() {
  const navigate = useNavigate();
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (pin1.length < 4 || pin1.length > 8) { setErr("PIN must be 4–8 digits"); return; }
    if (pin1 !== pin2) { setErr("PINs don't match"); return; }
    setBusy(true);
    try {
      await vapi.setPin(pin1);
      toast.success("PIN set — use it for quick login next time");
      navigate("/visitors");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not set PIN");
      setBusy(false);
    }
  };

  const pinField = (val, setter, testid) => (
    <input
      className="v-input" type="password" inputMode="numeric" autoComplete="off"
      value={val} maxLength={8}
      onChange={(e) => setter(e.target.value.replace(/\D/g, "").slice(0, 8))}
      placeholder="••••" data-testid={testid}
      style={{ letterSpacing: "0.4em", textAlign: "center", fontSize: 22 }}
    />
  );

  return (
    <>
      <Chrome title="Set up PIN" onBack={() => navigate("/visitors")} />
      <div className="v-screen">
        <div className="v-muted" style={{ marginBottom: 12 }}>
          Set a 4–8 digit PIN to sign in faster on this device — no need to type your password each time.
        </div>
        {err && <div className="v-error" data-testid="v-pin-error">{err}</div>}
        <label className="v-label">New PIN</label>
        {pinField(pin1, setPin1, "v-pin-1")}
        <label className="v-label">Confirm PIN</label>
        {pinField(pin2, setPin2, "v-pin-2")}
        <div style={{ height: 18 }} />
        <button className="v-btn" disabled={busy} onClick={save} data-testid="v-pin-save">
          {busy ? <Loader2 className="v-spin" size={18} /> : "Save PIN"}
        </button>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button className="v-link-btn" onClick={() => navigate("/visitors")} data-testid="v-pin-skip">
            Skip for now
          </button>
        </div>
      </div>
    </>
  );
}

function Home({ user, setUser }) {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const canViewAll = vAuth.can("visitor.view_all");
  const [bioRegistered, setBioRegistered] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const supportsWebAuthn = browserSupportsWebAuthn();

  useEffect(() => {
    vapi.stats("mine").then(setStats).catch(() => {});
    if (supportsWebAuthn) {
      vapi.webauthnStatus().then((r) => setBioRegistered(!!r.registered)).catch(() => {});
    }
  }, [supportsWebAuthn]);

  const logout = () => {
    vAuth.clear();
    setUser(null);
    nav("login");
  };

  const registerBiometric = async () => {
    setBioBusy(true);
    try {
      const optionsJSON = await vapi.webauthnRegisterBegin();
      const attResp = await startRegistration({ optionsJSON });
      await vapi.webauthnRegisterComplete(attResp);
      setBioRegistered(true);
      toast.success("Face ID / fingerprint enabled — use it to sign in next time");
    } catch (ex) {
      if (ex?.name === "NotAllowedError" || ex?.name === "AbortError") {
        toast.error("Set-up cancelled");
      } else {
        console.error("webauthn register error", ex);
        toast.error(ex?.response?.data?.detail || ex?.message || "Could not enable biometric login");
      }
    } finally {
      setBioBusy(false);
    }
  };

  const removeBiometric = async () => {
    setBioBusy(true);
    try {
      await vapi.webauthnClear();
      setBioRegistered(false);
      toast.success("Biometric login removed from this account");
    } catch {
      toast.error("Could not remove biometric login");
    } finally {
      setBioBusy(false);
    }
  };

  return (
    <>
      <Chrome title="Manik Leads" sub={user?.email} />
      <div className="v-screen">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 800, fontSize: 20 }}>
              Hi {(user?.name || user?.email || "").split("@")[0]} 👋
            </div>
            <div className="v-muted">Capture a new visitor</div>
          </div>
          <button className="v-link-btn" onClick={logout} data-testid="v-logout">
            <LogOut size={16} style={{ verticalAlign: "-3px" }} /> Sign out
          </button>
        </div>

        <div style={{ marginTop: 6 }}>
          <button className="v-link-btn" onClick={() => nav("pin-setup")} data-testid="v-set-pin" style={{ paddingLeft: 0 }}>
            Set / change PIN for faster login
          </button>
        </div>

        {supportsWebAuthn && (
          <div
            data-testid="v-biometric-card"
            style={{
              marginTop: 12, padding: "12px 14px", borderRadius: 12,
              background: "rgba(13,148,136,0.1)", border: "1px solid rgba(13,148,136,0.35)",
              display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <Fingerprint size={26} color="#2dd4bf" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Face ID / Fingerprint</div>
              <div className="v-muted" style={{ fontSize: 12 }}>
                {bioRegistered
                  ? "Enabled on this account — sign in with a tap."
                  : "Sign in instantly with biometrics — no password or PIN."}
              </div>
            </div>
            {bioRegistered ? (
              <button
                className="v-link-btn" disabled={bioBusy} onClick={removeBiometric}
                data-testid="v-biometric-remove" style={{ paddingRight: 0 }}
              >
                {bioBusy ? <Loader2 className="v-spin" size={16} /> : "Remove"}
              </button>
            ) : (
              <button
                className="v-btn v-btn--ghost" disabled={bioBusy} onClick={registerBiometric}
                data-testid="v-biometric-enable"
                style={{ width: "auto", padding: "8px 14px", flexShrink: 0 }}
              >
                {bioBusy ? <Loader2 className="v-spin" size={16} /> : "Enable"}
              </button>
            )}
          </div>
        )}

        <button
          className="v-tile v-tile--primary"
          style={{ width: "100%", marginTop: 14, alignItems: "flex-start" }}
          onClick={() => nav("badge")}
          data-testid="v-tile-badge"
        >
          <span className="v-tile-icon"><QrCode size={22} /></span>
          <span className="v-tile-label">Scan Event Badge</span>
          <span className="v-tile-hint">Scan the visitor's Bharat Buildcon QR — auto-fills everything</span>
        </button>

        <div className="v-tile-grid">
          <button className="v-tile" onClick={() => nav("scan")} data-testid="v-tile-scan">
            <span className="v-tile-icon"><ScanLine size={22} /></span>
            <span className="v-tile-label">Scan Card</span>
            <span className="v-tile-hint">Snap a business card</span>
          </button>
          <button className="v-tile" onClick={() => nav("new")} data-testid="v-tile-manual">
            <span className="v-tile-icon"><UserPlus size={22} /></span>
            <span className="v-tile-label">Add Manually</span>
            <span className="v-tile-hint">Type visitor details</span>
          </button>
        </div>

        <button
          className="v-list-item" style={{ marginTop: 12 }}
          onClick={() => nav("list")} data-testid="v-tile-list"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="v-tile-icon" style={{ width: 38, height: 38 }}><Users size={20} /></span>
            <div>
              <div className="v-li-name">{canViewAll ? "All Visitors" : "My Visitors"}</div>
              <div className="v-li-meta">View, search & edit captured leads</div>
            </div>
          </div>
          <ChevronLeft size={18} style={{ transform: "rotate(180deg)" }} />
        </button>

        {stats && (
          <div className="v-kpi-row">
            <div className="v-kpi"><div className="v-kpi-num" data-testid="v-stat-total">{stats.total}</div><div className="v-kpi-label">My Total</div></div>
            <div className="v-kpi"><div className="v-kpi-num" style={{ color: "#f87171" }}>{stats.by_rating?.hot ?? 0}</div><div className="v-kpi-label">Hot</div></div>
            <div className="v-kpi"><div className="v-kpi-num" style={{ color: "#fbbf24" }}>{stats.by_rating?.warm ?? 0}</div><div className="v-kpi-label">Warm</div></div>
            <div className="v-kpi"><div className="v-kpi-num" style={{ color: "#60a5fa" }}>{stats.by_rating?.cold ?? 0}</div><div className="v-kpi-label">Cold</div></div>
          </div>
        )}
      </div>
    </>
  );
}

function RatingChips({ value, onChange }) {
  const opt = (r, label) => (
    <button
      type="button"
      className={`v-chip v-chip--${r} ${value === r ? "v-chip--on" : ""}`}
      onClick={() => onChange(value === r ? "" : r)}
      data-testid={`v-rating-${r}`}
    >
      {label}
    </button>
  );
  return (
    <div className="v-rating-chips">
      {opt("hot", "Hot")}
      {opt("warm", "Warm")}
      {opt("cold", "Cold")}
    </div>
  );
}

function VisitorForm({ value, onChange }) {
  const f = (key, label, props = {}) => (
    <>
      <label className="v-label">{label}</label>
      <input
        className="v-input" value={value[key] || ""}
        onChange={(e) => onChange({ ...value, [key]: e.target.value })}
        data-testid={`v-field-${key}`} {...props}
      />
    </>
  );
  return (
    <div>
      {f("name", "Name *", { placeholder: "Visitor full name" })}
      {f("company", "Company")}
      {f("designation", "Designation")}
      {f("phone", "Phone", { type: "tel", inputMode: "tel" })}
      {f("email", "Email", { type: "email", inputMode: "email" })}
      {f("website", "Website")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>{f("city", "City")}</div>
        <div>{f("country", "Country")}</div>
      </div>
      <label className="v-label">Address</label>
      <textarea
        className="v-textarea" value={value.address || ""}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
        data-testid="v-field-address"
      />
      {f("products_interest", "Products of interest")}
      <label className="v-label">Rating</label>
      <RatingChips value={value.rating} onChange={(r) => onChange({ ...value, rating: r })} />
      <label className="v-label">Notes</label>
      <textarea
        className="v-textarea" value={value.notes || ""}
        onChange={(e) => onChange({ ...value, notes: e.target.value })}
        data-testid="v-field-notes"
      />
      {f("exhibition_name", "Exhibition / Event")}
    </div>
  );
}

function useSaveVisitor() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const save = async (form) => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return null;
    }
    // Hard duplicate block — refuse to create, take staff to the existing record.
    if (form.email?.trim() || form.phone?.trim()) {
      try {
        const dup = await vapi.checkDuplicate({ email: form.email, phone: form.phone });
        const m = dup?.matches?.[0];
        if (m) {
          const when = (m.captured_at || "").slice(0, 10);
          const who = m.captured_by_name ? ` by ${m.captured_by_name}` : "";
          const me = vAuth.getUser();
          const canOpen = vAuth.can("visitor.view_all") || m.captured_by_id === me?.id;
          if (canOpen) {
            toast.error(`Already captured${who}${when ? " on " + when : ""} — opening that record`);
            navigate(`/visitors/v/${m.id}`);
          } else {
            toast.error(`Already captured${who}${when ? " on " + when : ""}. Duplicate not saved.`);
          }
          return null;
        }
      } catch {
        /* pre-check is best-effort; backend also enforces a 409 */
      }
    }
    setBusy(true);
    try {
      const payload = { ...form, exhibition_name: form.exhibition_name || vAuth.getExhibition() || DEFAULT_EXHIBITION };
      if (payload.exhibition_name) vAuth.setExhibition(payload.exhibition_name);
      const res = await vapi.create(payload);
      toast.success("Visitor saved");
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { save, busy };
}

function CaptureManual() {
  const nav = useNavigate();
  const [form, setForm] = useState({ ...EMPTY, exhibition_name: vAuth.getExhibition() || DEFAULT_EXHIBITION });
  const { save, busy } = useSaveVisitor();

  const submit = async () => {
    const res = await save(form);
    if (res) nav("/visitors");
  };

  return (
    <>
      <Chrome title="Add Visitor" onBack={() => nav(-1)} />
      <div className="v-screen">
        <VisitorForm value={form} onChange={setForm} />
        <div style={{ height: 18 }} />
        <button className="v-btn" disabled={busy} onClick={submit} data-testid="v-save-manual">
          {busy ? <Loader2 className="v-spin" size={18} /> : "Save visitor"}
        </button>
      </div>
    </>
  );
}

function ScanCard() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState(null); // null until extracted
  const { save, busy } = useSaveVisitor();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setForm(null);
    try {
      const b64 = await fileToCompressedBase64(file);
      setPreview(`data:image/jpeg;base64,${b64}`);
      const res = await vapi.scanCard(b64);
      const fields = res.fields || {};
      const anyData = Object.values(fields).some((v) => (v || "").trim());
      setForm({
        ...EMPTY,
        ...fields,
        exhibition_name: vAuth.getExhibition() || DEFAULT_EXHIBITION,
        card_image_id: res.image_id,
        source: "card_scan",
      });
      if (anyData) toast.success("Card read — review & save");
      else toast.message("Couldn't read much — please fill the details");
    } catch (err) {
      toast.error("Scan failed — try again or add manually");
      setForm({ ...EMPTY, exhibition_name: vAuth.getExhibition() || DEFAULT_EXHIBITION });
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    const res = await save(form);
    if (res) nav("/visitors");
  };

  return (
    <>
      <Chrome title="Scan Card" onBack={() => nav(-1)} />
      <div className="v-screen">
        <input
          ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={onFile} style={{ display: "none" }} data-testid="v-card-file-input"
        />
        <input
          ref={galleryRef} type="file" accept="image/*"
          onChange={onFile} style={{ display: "none" }} data-testid="v-card-gallery-input"
        />

        {!preview && (
          <>
            <button
              className="v-tile v-tile--primary"
              style={{ alignItems: "center", textAlign: "center", minHeight: 200, justifyContent: "center" }}
              onClick={() => fileRef.current?.click()}
              data-testid="v-open-camera"
            >
              <span className="v-tile-icon" style={{ width: 56, height: 56 }}><Camera size={28} /></span>
              <span className="v-tile-label" style={{ fontSize: 18 }}>Take card photo</span>
              <span className="v-tile-hint">AI will read the contact details</span>
            </button>
            <button
              className="v-tile"
              style={{ alignItems: "center", textAlign: "center", justifyContent: "center", marginTop: 12 }}
              onClick={() => galleryRef.current?.click()}
              data-testid="v-open-gallery"
            >
              <span className="v-tile-icon" style={{ width: 48, height: 48 }}><ImageUp size={24} /></span>
              <span className="v-tile-label" style={{ fontSize: 17 }}>Choose from gallery</span>
              <span className="v-tile-hint">Pick an existing card photo</span>
            </button>
          </>
        )}

        {preview && <img src={preview} alt="card" className="v-preview-img" style={{ marginBottom: 12 }} />}

        {scanning && (
          <div className="v-card" style={{ textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }} data-testid="v-scanning">
            <Sparkles size={18} color="#5eead4" />
            <span>Reading card…</span>
            <Loader2 className="v-spin" size={16} />
          </div>
        )}

        {preview && !scanning && (
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="v-link-btn" onClick={() => fileRef.current?.click()} data-testid="v-rescan">
              <Camera size={14} style={{ verticalAlign: "-2px" }} /> Retake photo
            </button>
            <button className="v-link-btn" onClick={() => galleryRef.current?.click()} data-testid="v-rescan-gallery">
              <ImageUp size={14} style={{ verticalAlign: "-2px" }} /> Choose from gallery
            </button>
          </div>
        )}

        {form && !scanning && (
          <>
            <div className="v-success">Review the details below, edit anything, then save.</div>
            <VisitorForm value={form} onChange={setForm} />
            <div style={{ height: 18 }} />
            <button className="v-btn" disabled={busy} onClick={submit} data-testid="v-save-scan">
              {busy ? <Loader2 className="v-spin" size={18} /> : "Save visitor"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

function BadgeScan() {
  const nav = useNavigate();
  const scannerRef = useRef(null);
  const handlingRef = useRef(false);
  const [phase, setPhase] = useState("scan"); // scan | fetching | review
  const [form, setForm] = useState(null);
  const [manual, setManual] = useState("");
  const { save, busy } = useSaveVisitor();

  const handleUrl = async (text) => {
    if (handlingRef.current) return;
    const m = String(text || "").match(/https?:\/\/\S+/i);
    const url = m ? m[0] : String(text || "").trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error("That QR isn't a web link");
      return;
    }
    handlingRef.current = true;
    setPhase("fetching");
    try {
      const res = await vapi.scanBadge(url);
      setForm({
        ...EMPTY,
        ...(res.fields || {}),
        exhibition_name: vAuth.getExhibition() || DEFAULT_EXHIBITION,
        source: "badge_scan",
        pass_url: res.pass_url || url,
      });
      setPhase("review");
      toast.success("Badge read — review & save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that badge");
      handlingRef.current = false;
      setPhase("scan");
      setTimeout(() => scannerRef.current?.resume?.(), 400);
    }
  };

  const submit = async () => {
    const res = await save(form);
    if (res) nav("/visitors");
  };

  return (
    <>
      <Chrome title="Scan Event Badge" onBack={() => nav(-1)} />
      <div className="v-screen">
        {phase === "scan" && (
          <>
            <Scanner
              ref={scannerRef}
              onDecoded={handleUrl}
              hint="Point at the QR on the visitor's Bharat Buildcon badge"
            />
            <div className="v-divider" style={{ height: 1, background: "#0b4a45", margin: "16px 0" }} />
            <label className="v-label">Or paste the badge link</label>
            <input
              className="v-input"
              placeholder="https://pass.bharatbuildcon.com/..."
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              data-testid="v-badge-url-input"
            />
            <div style={{ height: 10 }} />
            <button className="v-btn v-btn--ghost" onClick={() => handleUrl(manual)} data-testid="v-badge-fetch">
              Fetch details
            </button>
          </>
        )}

        {phase === "fetching" && (
          <div
            className="v-card"
            style={{ textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            data-testid="v-badge-fetching"
          >
            <Sparkles size={18} color="#5eead4" />
            <span>Reading badge…</span>
            <Loader2 className="v-spin" size={16} />
          </div>
        )}

        {phase === "review" && form && (
          <>
            <div className="v-success">Badge details below — review, edit, then save.</div>
            <VisitorForm value={form} onChange={setForm} />
            <div style={{ height: 18 }} />
            <button className="v-btn" disabled={busy} onClick={submit} data-testid="v-badge-save">
              {busy ? <Loader2 className="v-spin" size={18} /> : "Save visitor"}
            </button>
          </>
        )}
      </div>
    </>
  );
}

function ratingDot(r) {
  const cls = r === "hot" ? "v-dot--hot" : r === "warm" ? "v-dot--warm" : r === "cold" ? "v-dot--cold" : "v-dot--none";
  return <span className={`v-dot ${cls}`} />;
}

function VisitorList() {
  const nav = useNavigate();
  const canViewAll = vAuth.can("visitor.view_all");
  const [scope, setScope] = useState("mine");
  const [q, setQ] = useState("");
  const [rating, setRating] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [opts, setOpts] = useState({ countries: [], cities: [] });
  const [sort, setSort] = useState("recent");
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await vapi.list({ scope, q, rating, country, city, sort });
      setItems(res.items || []);
      setCount(res.count || 0);
    } catch {
      toast.error("Could not load visitors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    vapi.stats(scope === "all" ? "all" : "mine")
      .then((s) => setOpts({ countries: s.countries || [], cities: s.cities || [] }))
      .catch(() => {});
  }, [scope]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, q, rating, country, city, sort]);

  return (
    <>
      <Chrome title={canViewAll && scope === "all" ? "All Visitors" : "My Visitors"} onBack={() => nav("/visitors")} />
      <div className="v-screen">
        {canViewAll && (
          <div className="v-toggle" style={{ marginBottom: 12 }}>
            <button className={scope === "mine" ? "on" : ""} onClick={() => setScope("mine")} data-testid="v-scope-mine">Mine</button>
            <button className={scope === "all" ? "on" : ""} onClick={() => setScope("all")} data-testid="v-scope-all">Everyone</button>
          </div>
        )}

        <div style={{ position: "relative", marginBottom: 8 }}>
          <Search size={16} style={{ position: "absolute", left: 12, top: 14, color: "#5eead4" }} />
          <input
            className="v-input" style={{ paddingLeft: 36 }} placeholder="Search name, company, email…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="v-search"
          />
        </div>

        <div className="v-rating-chips" style={{ marginBottom: 8 }}>
          {[["", "All"], ["hot", "Hot"], ["warm", "Warm"], ["cold", "Cold"]].map(([val, label]) => (
            <button
              key={val || "all"}
              type="button"
              className={`v-chip ${val ? `v-chip--${val}` : ""} ${rating === val ? "v-chip--on" : ""}`}
              onClick={() => setRating(val)}
              data-testid={`v-filter-${val || "all"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <select
          className="v-input" value={sort} onChange={(e) => setSort(e.target.value)}
          style={{ marginBottom: 10 }} data-testid="v-sort"
        >
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name A–Z</option>
          <option value="company">Company A–Z</option>
        </select>

        {(opts.countries.length > 0 || opts.cities.length > 0) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {opts.countries.length > 0 && (
              <select className="v-input" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="v-filter-country">
                <option value="">All countries</option>
                {opts.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            {opts.cities.length > 0 && (
              <select className="v-input" value={city} onChange={(e) => setCity(e.target.value)} data-testid="v-filter-city">
                <option value="">All cities</option>
                {opts.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        )}

        {!loading && (
          <div className="v-muted" style={{ marginBottom: 10 }} data-testid="v-list-count">
            {count} visitor{count === 1 ? "" : "s"}{q ? " matching" : ""}
          </div>
        )}

        {loading ? (
          <Loader2 className="v-spin" style={{ margin: "32px auto", display: "block" }} />
        ) : items.length === 0 ? (
          <div className="v-card" style={{ textAlign: "center" }} data-testid="v-empty">
            No visitors yet. Tap back and capture one.
          </div>
        ) : (
          <div data-testid="v-list">
            {items.map((it) => (
              <button key={it.id} className="v-list-item" onClick={() => nav(`/visitors/v/${it.id}`)} data-testid={`v-row-${it.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  {ratingDot(it.rating)}
                  <div style={{ minWidth: 0 }}>
                    <div className="v-li-name" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</div>
                    <div className="v-li-meta" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {[it.company, it.designation].filter(Boolean).join(" · ") || it.email || it.phone || "—"}
                    </div>
                    {scope === "all" && <div className="v-li-meta" style={{ opacity: 0.7 }}>by {it.captured_by_name}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function VisitorDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);

  const load = async () => {
    try {
      const d = await vapi.get(id);
      setDoc(d);
      setForm({ ...EMPTY, ...d });
      if (d.card_image_id) {
        vapi.cardObjectUrl(d.card_image_id).then(setCardUrl).catch(() => {});
      }
    } catch {
      toast.error("Could not load visitor");
      nav("/visitors/list");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveEdit = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const updated = await vapi.update(id, form);
      setDoc(updated);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this visitor?")) return;
    try {
      await vapi.remove(id);
      toast.success("Deleted");
      nav("/visitors/list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (!doc) {
    return (
      <>
        <Chrome title="Visitor" onBack={() => nav(-1)} />
        <div className="v-screen"><Loader2 className="v-spin" style={{ margin: "32px auto", display: "block" }} /></div>
      </>
    );
  }

  const Row = ({ icon, label, val }) =>
    val ? (
      <div style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #0b4a45" }}>
        <span style={{ color: "#5eead4", marginTop: 2 }}>{icon}</span>
        <div>
          <div className="v-muted" style={{ fontSize: 11 }}>{label}</div>
          <div>{val}</div>
        </div>
      </div>
    ) : null;

  // WhatsApp follow-up: open the visitor's chat with text pre-filled, AND make the
  // booth photo + message readily available so staff attach it in 1–2 taps.
  const handleWhatsApp = (e) => {
    e.preventDefault();
    const link = waLink(doc);
    if (!link) return;
    // 1) Copy the message (backup in case the chat doesn't pre-fill on some devices)
    try { navigator.clipboard?.writeText(followupMessage(doc)); } catch { /* ignore */ }
    // 2) Save the booth photo to the device so it's ready in the WhatsApp attach picker
    try {
      const a = document.createElement("a");
      a.href = BOOTH_PHOTO_URL;
      a.download = "manik-booth.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch { /* ignore */ }
    toast.success("Photo saved & message copied — in WhatsApp tap 📎 to attach the booth photo");
    // 3) Open the visitor's WhatsApp chat (number targeted, text pre-filled)
    window.open(link, "_blank", "noopener");
  };

  return (
    <>
      <Chrome title={editing ? "Edit Visitor" : "Visitor"} onBack={() => (editing ? setEditing(false) : nav(-1))} />
      <div className="v-screen">
        {editing ? (
          <>
            <VisitorForm value={form} onChange={setForm} />
            <div style={{ height: 18 }} />
            <button className="v-btn" disabled={busy} onClick={saveEdit} data-testid="v-save-edit">
              {busy ? <Loader2 className="v-spin" size={18} /> : "Save changes"}
            </button>
          </>
        ) : (
          <>
            <div className="v-card">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {ratingDot(doc.rating)}
                <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 800, fontSize: 22 }} data-testid="v-detail-name">{doc.name}</div>
              </div>
              {(doc.company || doc.designation) && (
                <div className="v-muted" style={{ marginTop: 4 }}>{[doc.designation, doc.company].filter(Boolean).join(" · ")}</div>
              )}
              <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="v-source-tag" data-testid="v-detail-source">via {SOURCE_LABEL[doc.source] || "Manual"}</span>
                {doc.pass_url && (
                  <a className="v-link-btn" href={doc.pass_url} target="_blank" rel="noreferrer" data-testid="v-open-pass">
                    Open official pass ↗
                  </a>
                )}
              </div>
            </div>

            {(waLink(doc) || mailtoLink(doc)) && (
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                {waLink(doc) && (
                  <a
                    href={waLink(doc)} target="_blank" rel="noreferrer"
                    onClick={handleWhatsApp}
                    className="v-btn"
                    style={{ flex: 1, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, background: "#25D366", color: "#04250f" }}
                    data-testid="v-followup-whatsapp"
                  >
                    <MessageCircle size={18} /> WhatsApp
                  </a>
                )}
                {mailtoLink(doc) && (
                  <a
                    href={mailtoLink(doc)}
                    className="v-btn v-btn--ghost"
                    style={{ flex: 1, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                    data-testid="v-followup-email"
                  >
                    <Mail size={18} /> Email
                  </a>
                )}
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                onClick={async () => {
                  const status = await shareBoothPhoto(doc);
                  if (status === "shared") return;
                  if (status === "unsupported") {
                    window.open(BOOTH_PHOTO_URL, "_blank", "noopener");
                    toast.message("Sharing not supported here — opened the photo so you can save & send it.");
                  } else if (status === "error") {
                    toast.error("Couldn't load the booth photo to share.");
                  }
                }}
                className="v-btn v-btn--ghost"
                style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                data-testid="v-share-booth-photo"
              >
                <ImageUp size={18} /> Share booth photo
              </button>
            </div>

            {cardUrl && <img src={cardUrl} alt="business card" className="v-preview-img" style={{ marginBottom: 12 }} data-testid="v-card-image" />}

            <div className="v-card">
              <Row icon={<Phone size={16} />} label="Phone" val={doc.phone} />
              <Row icon={<Mail size={16} />} label="Email" val={doc.email} />
              <Row icon={<Building2 size={16} />} label="Website" val={doc.website} />
              <Row icon={<Building2 size={16} />} label="City / Country" val={[doc.city, doc.country].filter(Boolean).join(", ")} />
              <Row icon={<Building2 size={16} />} label="Address" val={doc.address} />
              <Row icon={<Sparkles size={16} />} label="Products of interest" val={doc.products_interest} />
              <Row icon={<Pencil size={16} />} label="Notes" val={doc.notes} />
              <Row icon={<Users size={16} />} label="Exhibition" val={doc.exhibition_name} />
              <Row icon={<Users size={16} />} label="Captured by" val={doc.captured_by_name} />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button className="v-btn v-btn--ghost" onClick={() => setEditing(true)} data-testid="v-edit">
                <Pencil size={16} style={{ verticalAlign: "-3px" }} /> Edit
              </button>
              <button className="v-btn v-btn--danger" onClick={del} data-testid="v-delete">
                <Trash2 size={16} style={{ verticalAlign: "-3px" }} /> Delete
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
